import * as React from 'react'
import { FC, useMemo } from 'react'
import type { ENTITY_TYPES, LocationDTO } from '@sales-center/common'

import { SettingsPage, TabPanel } from '@sales-center/components'
import { CardVariants } from '../ContactCard'
import { ContactsList } from '../ContactsList'
import { RemoveContact } from '../RemoveContact/RemoveContact'
import { UpdateContactDrawer } from '../UpdateContact/UpdateContactDrawer'
import { useActions, useData, styles } from './hooks'
import AppBar from '@mui/material/AppBar'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import {
  AddServiceLocationDrawer,
  AddServiceLocationFormModel,
} from '@user/components/Location'

export type ContactsPageProps = {
  entityId: number
  entityType: ENTITY_TYPES
  canUpdateEntity: boolean
  initialTab?: number
}

export const ContactsPage: FC<ContactsPageProps> = (props) => {
  const { canUpdateEntity, entityId, entityType } = props

  const { detail, apiStatus, extContacts, wwmContacts } = useData(props)

  const {
    appName,

    activeTab,
    onChangeTab,

    onCloseDelete,
    deleteContactId,
    deleteContactOpen,

    updateContactId,
    showUpdateContact,
    onCloseUpdateContact,
    updateContactIsPrimary,

    onClickAddSecondaryContact,

    getMenuItems,
    isLocation,
    item = { data: {} },
  } = useActions(props, wwmContacts.length, detail)

  const { isBillToAndService, data } = item as any

  const isBillingLocation = useMemo(
    () => !isBillToAndService && (data as LocationDTO).billingLocation,
    [data, isBillToAndService],
  )

  const {
    locations: { serviceLocations },
  } = detail || { locations: { serviceLocations: [] } }

  const initValues = useMemo(() => {
    if (!data) return {}

    const {
      contactId,
      externalLinkId,
      externalLinkType,
      isSchedulable,
      latitude,
      locationCode,
      longitude,
      mobilePhone,
      primaryServiceLocation,
      salesTeamId,
      stateAbbreviation,
      street1,
      street2,
      tenantId,
      title,
      contact,
      ...preparedData
    } = data

    return {
      ...preparedData,
      address1: data.street1,
      address2: data.street2,
      billingLocation: isBillingLocation,
      branchId: Number(data.branchId),
      division: !isLocation || !data?.division ? '' : data.division,
      divisionId: !isLocation || !data?.divisionId ? '' : data.divisionId,
      id: isBillToAndService
        ? serviceLocations[0]?.id
        : !isLocation || !data?.id
          ? -1
          : data.id,
      lat: data.latitude,
      locationType: !isLocation || !data?.locationType ? '' : data.locationType,
      locationTypeId:
        !isLocation || !data?.locationTypeId ? '' : data.locationTypeId,
      lng: data.longitude,
      state: data.stateAbbreviation,
      updateBillTo: true,
      contactMethod: data.contact?.contactMethod ?? '',
      contactTime: data.contact?.contactTime
        ? data.contact.contactTime.split(',')
        : [],
    }
  }, [
    data,
    isBillToAndService,
    isBillingLocation,
    isLocation,
    serviceLocations,
  ])

  return (
    <>
      <SettingsPage
        title="Contacts"
        fullScreen
        padding="0"
        headContent={
          <AppBar
            sx={styles.appBar}
            color="default"
            elevation={1}
            position="sticky"
          >
            <Tabs value={activeTab} onChange={onChangeTab}>
              <Tab
                value={1}
                label={`WorkWave Marketing Contacts (${wwmContacts.length})`}
                sx={styles.tab}
                data-test-id="workWaveMarketingContactsTab"
              />
              <Tab
                value={2}
                label={`PestPac Contacts (${extContacts.length})`}
                sx={styles.tab}
                data-test-id="pestPacContactsTab"
              />
            </Tabs>
          </AppBar>
        }
      >
        <TabPanel tab={activeTab} value={1} sx={styles.tabPanel}>
          <Box sx={styles.helperText}>
            WorkWave Marketing Contacts can be created and managed from here.
            They can also be pushed to PestPac if the lead or opportunity is
            linked to a location. Best Contact Method and Best Contact Time do
            not transfer into PestPac.
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="space-between"
          >
            {canUpdateEntity && (
              <Box order={1}>
                <Button
                  color="primary"
                  onClick={onClickAddSecondaryContact}
                  type="submit"
                  variant="contained"
                  data-test-id="addContactButton"
                >
                  Add Contact
                </Button>
              </Box>
            )}
            {!detail || apiStatus === 'pending' ? (
              <CircularProgress />
            ) : (
              <Box order={0}>
                <ContactsList
                  {...props}
                  items={wwmContacts}
                  variant={CardVariants.wwm}
                  getMenuItems={getMenuItems}
                  primaryContactId={detail!.primaryContact.id}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel tab={activeTab} value={2} sx={styles.tabPanel}>
          <Box sx={styles.helperText}>
            PestPac Contacts are displayed here when a lead or opportunity is
            linked to a location. They can be edited in PestPac.
          </Box>

          {!extContacts || extContacts.length === 0 ? (
            <>No {appName || 'External'} contacts</>
          ) : !detail || apiStatus === 'pending' ? (
            <CircularProgress />
          ) : (
            <ContactsList
              {...props}
              items={extContacts}
              variant={CardVariants.pestpac}
              getMenuItems={getMenuItems}
              primaryContactId={detail!.primaryContact.id}
            />
          )}
        </TabPanel>
      </SettingsPage>
      {/* ========== D E L E T E   C O N T A C T ========== */}
      <RemoveContact
        {...props}
        onClose={onCloseDelete}
        open={deleteContactOpen}
        contactId={deleteContactId}
      />
      {/* ========== U P D A T E   C O N T A C T ========== */}
      {!!detail && !isLocation && (
        <UpdateContactDrawer
          detail={detail}
          entityId={entityId}
          entityType={entityType}
          open={showUpdateContact}
          contactId={updateContactId}
          onClose={onCloseUpdateContact}
          isPrimaryContact={updateContactIsPrimary}
        />
      )}
      {isLocation && (
        <AddServiceLocationDrawer
          {...props}
          initialValues={initValues as AddServiceLocationFormModel}
          open={showUpdateContact}
          isBillingLocation={isBillingLocation}
          entityBranchId={detail?.branchId || ''}
          onClose={onCloseUpdateContact}
        />
      )}
    </>
  )
}
ContactsPage.displayName = 'ContactsPage'
